import { useTranslation } from 'react-i18next';
import { ImageOff, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { useFileUrl } from '../useFileUrl';
import EquipmentNotes from './EquipmentNotes';
import EquipmentTypeBadge from './EquipmentTypeBadge';
import { FileChip, PolicyChip } from './LinkChips';
import { EquipmentValues } from '../types';

interface EquipmentItemCardProps {
    item: EquipmentValues;
    linksEnabled?: boolean;
    onEdit?: () => void;
    onRemove?: () => void;
}

export default function EquipmentItemCard({
    item,
    linksEnabled = true,
    onEdit,
    onRemove,
}: EquipmentItemCardProps) {
    const { t } = useTranslation();
    const imageUrl = useFileUrl(item.image);

    return (
        <div className="flex gap-4 rounded-lg border border-subtle bg-muted p-4 dark:bg-neutral-800">
            {imageUrl ? (
                <img
                    src={imageUrl}
                    alt={item.name}
                    className="h-16 w-16 shrink-0 rounded-md object-cover"
                />
            ) : (
                <div
                    aria-hidden="true"
                    className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-subtle bg-background text-subtle-foreground"
                >
                    <ImageOff className="h-4 w-4" />
                </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-3">
                <div className="flex items-end justify-between gap-2 border-b border-subtle pb-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-semibold tracking-tight">
                            {item.name}
                        </span>
                        <EquipmentTypeBadge type={item.type} />
                    </div>
                    {(onEdit || onRemove) && (
                        <div className="flex shrink-0 gap-1">
                            {onEdit && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    aria-label={t(
                                        'roomOverview.equipment.edit'
                                    )}
                                    title={t('roomOverview.equipment.edit')}
                                    className="h-7 w-7 border-input text-subtle-foreground hover:text-foreground [&_svg]:!size-3.5"
                                    onClick={onEdit}
                                >
                                    <Pencil aria-hidden="true" />
                                </Button>
                            )}
                            {onRemove && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    aria-label={t(
                                        'roomOverview.equipment.remove'
                                    )}
                                    title={t('roomOverview.equipment.remove')}
                                    className="h-7 w-7 border-input text-subtle-foreground hover:text-destructive [&_svg]:!size-3.5"
                                    onClick={onRemove}
                                >
                                    <Trash2 aria-hidden="true" />
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                <EquipmentNotes html={item.notes} />
                {item.policies.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {item.policies.map((policy) => (
                            <PolicyChip
                                key={policy._id}
                                policy={policy}
                                interactive={linksEnabled}
                            />
                        ))}
                    </div>
                )}
                {item.files.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {item.files.map((file) => (
                            <FileChip
                                key={file._id}
                                file={file}
                                interactive={linksEnabled}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
